// ============================================
// ConverseTech Frontend - WebSocket Hook
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import type { WSClientMessage, WSServerMessage } from '@conversetech/shared';

// Maximum number of messages to queue while disconnected
const MAX_QUEUE_SIZE = 1000;

interface UseWebSocketOptions {
  onMessage: (data: WSServerMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
}

export function useWebSocket({
  onMessage,
  onOpen,
  onClose,
  onError,
}: UseWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const connectingRef = useRef(false);
  const messageQueueRef = useRef<WSClientMessage[]>([]);

  // Use refs for callbacks to avoid recreating connect function
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
  }, [onMessage, onOpen, onClose, onError]);

  // Flush queued messages when connected
  const flushQueue = useCallback((ws: WebSocket) => {
    const queue = messageQueueRef.current;
    if (queue.length > 0) {
      for (const message of queue) {
        ws.send(JSON.stringify(message));
      }
      messageQueueRef.current = [];
    }
  }, []);

  const connect = useCallback((url: string) => {
    // Prevent multiple simultaneous connection attempts
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING ||
        connectingRef.current) {
      return;
    }

    connectingRef.current = true;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      connectingRef.current = false;
      setIsConnected(true);
      // Flush any queued messages
      flushQueue(ws);
      onOpenRef.current?.();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WSServerMessage;
        onMessageRef.current(data);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      connectingRef.current = false;
      setIsConnected(false);
      wsRef.current = null;
      onCloseRef.current?.();
    };

    ws.onerror = (error) => {
      connectingRef.current = false;
      console.error('WebSocket error:', {
        type: (error as Event).type,
        url,
        readyState: ws.readyState,
        // Common codes: 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
      });
      onErrorRef.current?.(error);
    };

    wsRef.current = ws;
  }, [flushQueue]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
    }
    // Clear queue on disconnect
    messageQueueRef.current = [];
  }, []);

  const sendMessage = useCallback((message: WSClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      // Queue message if not connected (for transcript updates, etc.)
      if (messageQueueRef.current.length < MAX_QUEUE_SIZE) {
        messageQueueRef.current.push(message);
      } else {
        console.warn('WebSocket message queue full, dropping message');
      }
    }
  }, []);

  return {
    connect,
    disconnect,
    sendMessage,
    isConnected,
  };
}
